// (7) Scaffold ladder (sidebar on desktop, horizontal scroll on mobile).
// 4 rungs: Ground Zero → Build Up → Bridge → Target, colored along the same
// pink→orange→blue→green mastery progression used everywhere else.
const NODES: { label: string; desc: string; varKey: string }[] = [
  { label: 'Ground Zero', desc: 'Rebuild the basics from scratch.', varKey: 'pink' },
  { label: 'Build Up', desc: 'Worked examples at this level.', varKey: 'orange' },
  { label: 'Bridge', desc: 'Practice questions, no jump yet.', varKey: 'blue' },
  { label: 'Target', desc: 'Back to the original question.', varKey: 'green' },
];

interface Props {
  current: number;
  completed: Set<number>;
  sidebranched: Set<number>;
  onSelect: (level: number) => void;
}

export function LevelProgressLadder({ current, completed, sidebranched, onSelect }: Props) {
  return (
    <nav aria-label="Scaffold progress" className="lg:w-64">
      <ol className="flex gap-2 overflow-x-auto lg:flex-col lg:gap-2.5">
        {NODES.map((node, level) => {
          const isDone = completed.has(level);
          const isActive = current === level;
          const reachable = level <= current || isDone;
          const tag = isDone ? 'Done' : isActive ? 'You are here' : reachable ? 'Available' : 'Locked';
          const dotBg = isDone ? 'var(--green-border)' : isActive ? `var(--${node.varKey}-border)` : 'var(--line-strong)';

          return (
            <li key={level} className="shrink-0 lg:shrink">
              <button
                onClick={() => reachable && onSelect(level)}
                disabled={!reachable}
                className="flex w-full items-center gap-3 rounded-2xl p-3 text-left transition"
                style={{
                  background: isActive ? `var(--${node.varKey}-bg)` : 'var(--surface-card)',
                  border: `1px solid ${isActive ? `var(--${node.varKey}-border)` : 'var(--border-default)'}`,
                  opacity: reachable ? 1 : 0.55,
                  cursor: reachable ? 'pointer' : 'not-allowed',
                }}
              >
                <span
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-extrabold text-white"
                  style={{ background: dotBg }}
                >
                  {isDone ? '✓' : level + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-fs-13.5 font-bold" style={{ color: 'var(--text-strong)' }}>{node.label}</span>
                    {sidebranched.has(level) && <span className="text-xs" title="Explored side-branch">↳</span>}
                  </div>
                  <div className="hidden text-fs-11.5 font-medium lg:block" style={{ color: 'var(--ink-3)' }}>{node.desc}</div>
                </div>
                <span
                  className="hidden flex-none rounded-full px-2 py-0.5 text-fs-10.5 font-bold lg:inline-block"
                  style={{
                    background: isDone ? 'var(--green-bg)' : isActive ? `var(--${node.varKey}-border)` : 'var(--paper-sunk)',
                    color: isDone ? 'var(--green-text)' : isActive ? '#fff' : 'var(--ink-3)',
                  }}
                >
                  {tag}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
